import DatePicker from '~/components/ui/form/date-picker/index.vue'
import { Selectbox, USwitch, UTextarea, ServiceIntervals } from '#components'
import PhoneNumberInput from '~/components/ui/phone-number-input/index.vue'
import AddressSearchField from '~/components/ui/form/address-search-field/index.vue'
import { countryFormats } from '../helpers/countryFormats'
import * as yup from 'yup'
import FileUploader from '~/components/ui/file-uploader/index.vue'
import { isMobile } from '~/helpers/breakpoints'
import Input from '~/components/ui/input/index.vue'
import Barcodes from '~/components/ui/form/barcodes/index.vue'
import Barcode from '~/components/ui/form/barcode/index.vue'
import BoardStatusCell from '~/components/board-status-cell/index.vue'
import NameAvatar from '~/components/ui/name-avatar/index.vue'
import ClientServiceTokenCheck from '~/components/client-service/token-check/index.vue'
import ShipmentLinkCell from '~/components/shipment-link-cell/index.vue'
import TimezoneSelect from '~/components/ui/form/timezone-select/index.vue'

const modules = import.meta.glob('../forms-build/*.js')

function kebabToCamel(str) {
  if (!str) return ''
  return str.replace(/[-\/]([a-zA-Z])/g, (_, char) => char.toUpperCase())
}

const BOOLEAN_SELECTBOX_PROPS = {
  options: [
    { value: 'true', title: 'true' },
    { value: 'false', title: 'false' },
  ],
}

const route = useRoute()
const selectedBoardUuid = computed(() => {
  const value = route.query?.['filter[boardUuid]']
  if (!value) return null
  return Array.isArray(value) ? value[0] : String(value)
})

const loadSchemeValidation = async (tableName) => {
  const entry = Object.entries(modules).find(([path]) =>
    path.endsWith(`${tableName}.js`)
  )
  if (entry) {
    const mod = await entry[1]()
    return mod[tableName] || {}
  }
  return {}
}

export const useScheme = async (tableNameRow) => {
  const tableName = kebabToCamel(tableNameRow)
  const route = useRoute()
  const DEFAULT_TEMPLATE = !isMobile.value ? 'repeat(3, 1fr)' : '1fr!important'

  const schemeValidation = await loadSchemeValidation(tableName)

  const schemeData = [
    {
      forFields: ['id'],
      classList: 'opacity-50 pointer-events-none',
    },
    {
      forFields: ['isNDS'],
      component: USwitch,
      fullsize: true,
      classList: 'mt-1.5',
    },
    {
      forFields: ['isActive'],
      component: USwitch,
      fullsize: true,
      classList: 'mt-1.5',
    },
    {
      forFields: ['NDSValue'],
      component: Input,
      props: {
        number: true,
      },
      condition: (obj) => {
        const isNDS = obj.find((e) => {
          if (e.key === 'isNDS') {
            return e
          }
        })
        return isNDS?.value
      },
    },
    {
      forFields: [
        'created_at',
        'updated_at',
        'deleted_at',
        'updated_by',
        'created_by',
        'createdBy',
        'updatedBy',
        'warehouseman',
      ],
      hidden: true,
      table: {
        width: 300,
        rangeFilters: true,
      },
    },
    {
      forFields: ['founded_on_basis'],
      fullsize: true,
      component: UTextarea,
      classList: 'w-full',
    },
    {
      forFields: ['ogrn_date'],
      component: DatePicker,
      table: {
        width: 200,
      },
    },
    {
      forFields: ['image'],
      component: FileUploader,
      props: {
        multiple: true,
        massUploadMode: true,
      },
    },
    {
      forFields: ['contact_person_phone', 'phone'],
      fullsize: false,
      component: PhoneNumberInput,
      props: {
        countryFormats,
      },
      table: {
        component: PhoneNumberInput,
        width: 180,
        props: {
          ghost: true,
          countryFormats,
        },
      },
    },
    {
      forFields: ['regionTypeUuid', 'regionType'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        lookup: 'regionType',
      },
    },
    {
      forFields: ['productTypeUuid', 'productType'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        lookup: 'productType',
      },
    },
    {
      forFields: ['productCategoryUuid', 'productCategory'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        lookup: 'productCategory',
      },
    },
    {
      forFields: ['managerUuid', 'manager'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        displayExpr: 'name',
        lookup: 'manager',
        clipText: true,
      },
      table: {
        component: NameAvatar,
        width: 180,
        props: {
          lookup: 'manager',
        },
      },
    },
    {
      forFields: ['timezone'],
      component: TimezoneSelect,
      fullsize: true,
    },
    {
      forFields: ['shipment'],
      table: {
        component: ShipmentLinkCell,
        width: 150,
        interactive: true,
      },
    },
    {
      forFields: ['barcode'],
      component: Barcode,
      fullsize: true,
    },
    {
      forFields: ['barcodes'],
      component: Barcodes,
      fullsize: true,
    },
    {
      forFields: ['file'],
      component: FileUploader,
      fullsize: true,
      props: {
        multiple: false,
        massUploadMode: true,
      },
    },
    {
      forFields: ['files'],
      component: FileUploader,
      fullsize: true,
      props: {
        multiple: true,
        massUploadMode: true,
      },
    },
    {
      forFields: [
        'packageWeight',
        'packageHeight',
        'packageLength',
        'packageWidth',
      ],
      component: Input,
      props: {
        number: true,
      },
    },
    {
      forFields: ['isActiveUuid'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        displayExpr: 'title',
        lookup: 'isActive',
      },
    },
    {
      forFields: ['serviceUuid'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        displayExpr: 'code',
        lookup: 'service',
      },
    },
    {
      forFields: ['statusUuid'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        displayExpr: 'title',
        lookup: 'status',
      },
    },
    {
      forFields: ['typeUuid'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        displayExpr: 'title',
        lookup: 'type',
      },
    },
    {
      forFields: ['clientUuid'],
      component: Selectbox,
      props: {
        width: 260,
        valueExpr: 'id',
        displayExpr: 'fullName',
        lookup: 'client',
        clipText: true,
      },
      table: {
        component: NameAvatar,
        width: 220,
        props: {
          lookup: 'client',
        },
      },
    },
    {
      forFields: ['warehouseUuid'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        displayExpr: 'title',
        lookup: 'warehouse',
      },
    },
    {
      forFields: ['serviceUuid'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        displayExpr: 'name',
        lookup: 'serviceUuid',
      },
    },
    {
      forFields: ['roleUuid'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        displayExpr: 'name',
        lookup: 'role',
        width: 240,
      },
    },
    {
      forFields: ['warehousemanUuid'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        displayExpr: 'fullName',
        lookup: 'warehouseman',
      },
      condition: () => {
        tableName !== 'warehouse'
      },
    },
    {
      forFields: ['settings'],
      component: Selectbox,
      props: {
        valueExpr: 'value',
        options: [
          { key: '1', value: 'Да' },
          { key: '0', value: 'Нет' },
        ],
      },
      condition: () => {
        return tableName === 'client-service'
      },
    },
    {
      forFields: ['isArciz'],
      component: USwitch,
      fullsize: true,
      classList: 'mt-1.5',
      props: BOOLEAN_SELECTBOX_PROPS,
      toPayload: (value) => (value ? 'isArciz' : ''),
      fromPayload: (value) => value === 'isArciz',
    },
    {
      forFields: ['isProd'],
      component: USwitch,
      fullsize: true,
      classList: 'mt-1.5',
      props: BOOLEAN_SELECTBOX_PROPS,
    },
    {
      forFields: ['apiToken'],
      component: ClientServiceTokenCheck,
      fullsize: true,
    },
    {
      forFields: ['address'],
      component: AddressSearchField,
      fullsize: true,
    },
    {
      forFields: ['warehouseCell', 'product', 'pallets', 'productsPerPallet'],
      condition: () => false,
    },
    {
      forFields: ['cellPlacementType'],
      component: Selectbox,
      props: {
        lookup: 'cellPlacementType',
        valueExpr: 'id',
        displayExpr: 'title',
      },
      // setDisabled: (isCreate) => (!isCreate ? true : false),
    },
    {
      forFields: ['storageAccountingType'],
      component: Selectbox,
      props: {
        lookup: 'storageAccountingType',
        valueExpr: 'id',
        displayExpr: 'title',
      },
    },
    {
      forFields: [
        'section',
        'height',
        'width',
        'length',
        'floor',
        'boxMultiplicity',
        'nmId',
      ],
      component: Input,
      props: {
        number: true,
      },
    },
    {
      forFields: ['boardUuid'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        displayExpr: 'code',
        lookup: 'board',
      },
    },
    {
      forFields: ['boardStatus'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        displayExpr: 'code',
        lookup: 'boardStatus',
      },
    },
    {
      forFields: ['boardStatusUuid'],
      component: Selectbox,
      props: reactive({
        lookup: 'boardWithStatus',
        valueExpr: 'id',
        displayExpr: 'title',
        lookupVariable: computed(() => ({
          field: 'id',
          condition: selectedBoardUuid.value,
          name: 'boardStatuses',
        })),
      }),
      filter: {
        component: Selectbox,
        props: { lookup: 'boardStatus' },
      },
      table: { component: BoardStatusCell },
    },
    {
      forFields: ['warehouseServiceCategory', 'productCategory'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
      },
    },
    {
      forFields: ['warehouseServiceCategoryUuid'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        lookup: 'warehouseServiceCategory',
      },
    },
    {
      forFields: ['warehouseServiceStageTypeUuids'],
      component: Selectbox,
      props: {
        valueExpr: 'id',
        lookup: 'warehouseServiceStageType',
        multiselect: true,
      },
    },
    {
      forFields: ['autoApply'],
      component: USwitch,
      classList: 'mt-1.5',
    },
    {
      forFields: ['warehouseServiceIntervals'],
      component: ServiceIntervals,
      fullsize: true,
      props: {
        resource: 'forms.warehouse-service.serviceIntervals',
      },
    },
  ]

  const currentScheme = (fieldName) =>
    schemeData.find((e) => e.forFields.includes(fieldName))

  const getHidden = (fieldName) => currentScheme(fieldName)?.hidden || false
  const getProps = (fieldName) => currentScheme(fieldName)?.props || null
  const getFiltersProps = (fieldName) =>
    currentScheme(fieldName)?.filter?.props || null
  const dataEditorScheme = (fieldName) =>
    currentScheme(fieldName)?.component || null
  const fieldSize = (fieldName) => currentScheme(fieldName)?.fullsize
  const classList = (fieldName) => currentScheme(fieldName)?.classList
  const getFilter = (fieldName) => currentScheme(fieldName)?.filter
  const getTableScheme = (fieldName) => currentScheme(fieldName) || null

  const getSchemaByTableName = (isCreate) => {
    const validation = schemeValidation?.validation
    if (typeof validation === 'function') return validation(isCreate)
    return validation || yup.object({})
  }

  const getDisabled = (fieldName, isCreate) =>
    currentScheme(fieldName)?.setDisabled
      ? currentScheme(fieldName).setDisabled(isCreate)
      : false

  const gridTemplateByName = () =>
    schemeValidation?.template || DEFAULT_TEMPLATE
  const getDataTemplateLocal = () => schemeValidation?.blank
  const getTabName = () => schemeValidation?.tabName || null
  const getDataMobileTableFixedRows = () => schemeValidation?.fixedRows
  const isSwitchField = (fieldName) =>
    currentScheme(fieldName)?.component === USwitch

  const getCondition = (fieldName, obj) =>
    currentScheme(fieldName)?.condition
      ? currentScheme(fieldName).condition(obj)
      : true

  const getValueByFilter = (paramName) =>
    computed(() => {
      const value = route.query?.[`filter[${paramName}]`]
      if (!value) return null
      return Array.isArray(value) ? value[0] : String(value)
    })

  const getToPayload = (fieldName) => currentScheme(fieldName)?.toPayload || null
  const getFromPayload = (fieldName) =>
    currentScheme(fieldName)?.fromPayload || null

  return {
    getSchemaByTableName,
    dataEditorScheme,
    gridTemplateByName,
    fieldSize,
    classList,
    getProps,
    getDataTemplateLocal,
    getHidden,
    isSwitchField,
    getCondition,
    getTableScheme,
    getDataMobileTableFixedRows,
    getTabName,
    getFilter,
    getFiltersProps,
    getValueByFilter,
    getDisabled,
    getToPayload,
    getFromPayload,
  }
}
